import { logger } from 'modules/logger';
import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Web Speech "advisor" used only to provide an alternate transcript for picklist matching.
 * It does not insert text directly into the editor; callers decide whether to use its transcript.
 */
type SpeechRecognitionConstructor = new () => {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
};

type SpeechRecognitionAlternativeLike = {
  transcript?: string;
};

type SpeechRecognitionResultLike = {
  [index: number]: SpeechRecognitionAlternativeLike | undefined;
};

type SpeechRecognitionResultListLike = ArrayLike<SpeechRecognitionResultLike | undefined>;

type SpeechRecognitionResultEventLike = {
  results?: SpeechRecognitionResultListLike | null;
  resultIndex?: number;
};

type SpeechRecognitionErrorEventLike = {
  error?: string;
};

type WindowWithSpeechRecognition = Window & {
  SpeechRecognition?: SpeechRecognitionConstructor;
  webkitSpeechRecognition?: SpeechRecognitionConstructor;
};

const getSpeechRecognitionConstructor = (): SpeechRecognitionConstructor | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const speechWindow = window as WindowWithSpeechRecognition;
  const maybeCtor = speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
  return typeof maybeCtor === 'function' ? (maybeCtor as SpeechRecognitionConstructor) : null;
};

const toJoinedTranscriptFromIndex = (
  results: Nullable<SpeechRecognitionResultListLike>,
  startIndex: number
): string =>
  Array.from(results ?? [])
    .slice(Math.max(0, startIndex))
    .map((result) => result?.[0]?.transcript ?? '')
    .join(' ')
    .trim();

const toJoinedTranscript = (results: Nullable<SpeechRecognitionResultListLike>): string =>
  toJoinedTranscriptFromIndex(results, 0);

const mergeDeferredTranscript = (previous: string, transcript: string): string => {
  if (previous === '') {
    return transcript.trim();
  }

  const lowerPrevious = previous.toLowerCase();
  const lowerTranscript = transcript.toLowerCase();
  const isDuplicateOrContainedByPrevious =
    lowerPrevious === lowerTranscript ||
    lowerPrevious.endsWith(lowerTranscript) ||
    lowerPrevious.startsWith(lowerTranscript);

  if (isDuplicateOrContainedByPrevious) {
    return previous.trim();
  }

  const isPreviousContainedByTranscript =
    lowerTranscript.startsWith(lowerPrevious) || lowerTranscript.endsWith(lowerPrevious);

  if (isPreviousContainedByTranscript) {
    return transcript.trim();
  }

  return `${previous} ${transcript}`.trim();
};

const getTranscriptFromRecognitionEvent = (event: SpeechRecognitionResultEventLike): string => {
  const fullTranscript = toJoinedTranscript(event?.results);
  if (fullTranscript === '') return '';

  const hasResultIndex = typeof event?.resultIndex === 'number' && event.resultIndex >= 0;
  if (!hasResultIndex) {
    return fullTranscript;
  }

  // resultIndex points at newly updated results.
  const resultIndex = event.resultIndex ?? 0;
  const latestTranscript = toJoinedTranscriptFromIndex(event?.results, resultIndex);
  return latestTranscript === '' ? fullTranscript : latestTranscript;
};

export const useWebSpeechPicklistAdvisor = ({
  enabled,
}: {
  enabled: boolean;
}): {
  startPicklistAdvisorSession: (args: { dictationId: string; picklistId: string }) => void;
  stopPicklistAdvisorSession: (dictationId: string) => void;
  setPicklistAdvisorDeferred: (args: { dictationId: string; deferred: boolean }) => void;
  getPicklistAdvisorTranscript: (dictationId: string) => string | null;
} => {
  // We keep a single SpeechRecognition instance and reuse it across dictation sessions.
  const recognitionRef = useRef<InstanceType<SpeechRecognitionConstructor> | null>(null);
  // Tracks which dictation currently owns transcript updates from recognition callbacks.
  const activeDictationIdRef = useRef<string | null>(null);
  // Used by onend to decide whether to auto-restart recognition after unexpected stops.
  const shouldKeepRunningRef = useRef(false);
  // Counts intentional stop() calls. The next onend for each stop should not auto-restart.
  const pendingIntentionalStopsRef = useRef(0);
  // Latest transcript segment from the current onresult event (non-deferred mode reads this).
  const latestTranscriptsByDictationIdRef = useRef<Map<string, string>>(new Map());
  // Transcript that may accumulate across updates while dictation is deferred.
  // This is what getPicklistAdvisorTranscript returns to matching logic.
  const accumulatedTranscriptsByDictationIdRef = useRef<Map<string, string>>(new Map());
  // Deferral flag per dictation id: true => append updates, false => keep only latest snapshot.
  const deferredByDictationIdRef = useRef<Map<string, boolean>>(new Map());
  const hasLoggedUnsupportedRef = useRef(false);

  const log = useCallback(
    (message: string, payload?: Record<string, unknown>) => {
      if (!enabled) return;
      logger.debug(`[useWebSpeechPicklistAdvisor] ${message}`, payload ?? {});
    },
    [enabled]
  );

  const speechRecognitionCtor = useMemo(() => getSpeechRecognitionConstructor(), []);

  const getOrCreateRecognition = useCallback(() => {
    if (!enabled || speechRecognitionCtor == null) {
      return null;
    }

    if (recognitionRef.current != null) {
      return recognitionRef.current;
    }

    const recognition = new speechRecognitionCtor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event: SpeechRecognitionResultEventLike) => {
      const dictationId = activeDictationIdRef.current;
      if (dictationId == null) return;

      const transcript = getTranscriptFromRecognitionEvent(event);
      if (transcript === '') return;

      const isDeferred = deferredByDictationIdRef.current.get(dictationId) === true;
      latestTranscriptsByDictationIdRef.current.set(dictationId, transcript);
      if (isDeferred) {
        // Deferred: keep appending so partial command phrases can be completed across updates.
        const previous = accumulatedTranscriptsByDictationIdRef.current.get(dictationId) ?? '';
        accumulatedTranscriptsByDictationIdRef.current.set(
          dictationId,
          mergeDeferredTranscript(previous, transcript)
        );
      } else {
        // Non-deferred: overwrite so stale historical advisor text does not bleed into matching.
        accumulatedTranscriptsByDictationIdRef.current.set(dictationId, transcript);
      }
      log('transcript_update', { dictationId, transcript });
    };

    recognition.onerror = (event: SpeechRecognitionErrorEventLike) => {
      log('error', {
        dictationId: activeDictationIdRef.current,
        error: event?.error ?? 'unknown',
      });
    };

    recognition.onend = () => {
      if (pendingIntentionalStopsRef.current > 0) {
        pendingIntentionalStopsRef.current -= 1;
        log('stopped_intentional', {
          dictationId: activeDictationIdRef.current,
          remainingPendingStops: pendingIntentionalStopsRef.current,
        });
        return;
      }

      const dictationId = activeDictationIdRef.current;
      if (!shouldKeepRunningRef.current || dictationId == null) {
        log('stopped', { dictationId });
        return;
      }

      // Browsers may end recognition unexpectedly; restart while the dictation session is active.
      try {
        recognition.start();
        log('restarted', { dictationId });
      } catch (error) {
        log('restart_failed', { dictationId, error });
      }
    };

    recognitionRef.current = recognition;
    return recognition;
  }, [enabled, log, speechRecognitionCtor]);

  const startPicklistAdvisorSession = useCallback(
    ({ dictationId, picklistId }: { dictationId: string; picklistId: string }) => {
      if (!enabled) return;
      const recognition = getOrCreateRecognition();
      if (recognition == null) {
        if (!hasLoggedUnsupportedRef.current) {
          hasLoggedUnsupportedRef.current = true;
          log('unsupported', { dictationId, picklistId });
        }
        return;
      }

      const previousId = activeDictationIdRef.current;
      if (previousId != null && previousId !== dictationId) {
        shouldKeepRunningRef.current = false;
        try {
          pendingIntentionalStopsRef.current += 1;
          recognition.stop();
        } catch (_error) {
          // no-op; start below will recover
          pendingIntentionalStopsRef.current = Math.max(0, pendingIntentionalStopsRef.current - 1);
        }
      }

      activeDictationIdRef.current = dictationId;
      shouldKeepRunningRef.current = true;

      if (!latestTranscriptsByDictationIdRef.current.has(dictationId)) {
        // Initialize map entry so callers can distinguish "known session with no transcript yet".
        latestTranscriptsByDictationIdRef.current.set(dictationId, '');
      }
      if (!accumulatedTranscriptsByDictationIdRef.current.has(dictationId)) {
        accumulatedTranscriptsByDictationIdRef.current.set(dictationId, '');
      }
      deferredByDictationIdRef.current.set(dictationId, false);

      try {
        recognition.start();
      } catch (_error) {
        // If recognition is already running, this throws InvalidStateError; we can ignore it.
      }

      log('started', { dictationId, picklistId });
    },
    [enabled, getOrCreateRecognition, log]
  );

  const stopPicklistAdvisorSession = useCallback(
    (dictationId: string) => {
      if (!enabled) return;
      // Always clear per-dictation transcript cache, even if this dictation is no longer active.
      latestTranscriptsByDictationIdRef.current.delete(dictationId);
      accumulatedTranscriptsByDictationIdRef.current.delete(dictationId);
      deferredByDictationIdRef.current.delete(dictationId);
      if (activeDictationIdRef.current !== dictationId) return;

      shouldKeepRunningRef.current = false;
      activeDictationIdRef.current = null;

      if (recognitionRef.current != null) {
        try {
          pendingIntentionalStopsRef.current += 1;
          recognitionRef.current.stop();
        } catch (_error) {
          // no-op
          pendingIntentionalStopsRef.current = Math.max(0, pendingIntentionalStopsRef.current - 1);
        }
      }

      log('stop_requested', { dictationId });
    },
    [enabled, log]
  );

  // Deferral is toggled by text-processing when a stable-text chunk is incomplete and may be
  // merged with a following chunk (for example "pick ..." followed by the option words).
  // - While deferred=true, advisor transcript keeps accumulating across result updates/chunks.
  // - When deferred=false, we collapse back to the latest segment so stale history does not
  //   influence matching for the next independent command.
  const setPicklistAdvisorDeferred = useCallback(
    ({ dictationId, deferred }: { dictationId: string; deferred: boolean }) => {
      if (!enabled) return;

      deferredByDictationIdRef.current.set(dictationId, deferred);
      if (!deferred) {
        // Leaving deferral mode resets accumulated history back to the latest segment.
        const latest = latestTranscriptsByDictationIdRef.current.get(dictationId) ?? '';
        accumulatedTranscriptsByDictationIdRef.current.set(dictationId, latest);
      }
    },
    [enabled]
  );

  const getPicklistAdvisorTranscript = useCallback(
    (dictationId: string): string | null => {
      if (!enabled) return null;
      const value = (accumulatedTranscriptsByDictationIdRef.current.get(dictationId) ?? '').trim();
      return value === '' ? null : value;
    },
    [enabled]
  );

  useEffect(
    () => () => {
      // Ensure we stop the browser recognition session when this hook unmounts.
      shouldKeepRunningRef.current = false;
      activeDictationIdRef.current = null;
      if (recognitionRef.current != null) {
        try {
          pendingIntentionalStopsRef.current += 1;
          recognitionRef.current.stop();
        } catch (_error) {
          // no-op
          pendingIntentionalStopsRef.current = Math.max(0, pendingIntentionalStopsRef.current - 1);
        }
      }
    },
    []
  );

  return {
    startPicklistAdvisorSession,
    stopPicklistAdvisorSession,
    setPicklistAdvisorDeferred,
    getPicklistAdvisorTranscript,
  };
};
